import { ControlProps, defaultDateFormat } from "@jsonforms/core";
import { createOnChangeHandler } from "@jsonforms/material-renderers";
import { useJsonForms, withJsonFormsControlProps } from "@jsonforms/react";
import { FormHelperText } from "@mui/material";
import Grid from "@mui/material/Grid";
import {
  DatePicker,
  LocalizationProvider,
  TimePicker,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { Constants, useAppointmentErrors, useTranslation } from "hhn-commons";
import React, { useEffect, useState } from "react";
import { timePickerViewRenderers } from "../../../common/ViewRenderers/timePickerViewRenderers";
import { ValidationModeJFC } from "@home-health-notify/templates";

const AppointmentPickerRenderer = ({
  path,
  data,
  handleChange,
  uischema,
  schema,
  visible,
}: ControlProps) => {
  const { core } = useJsonForms();
  const { t } = useTranslation();
  const errors = core?.errors ?? [];
  const validationMode = core?.validationMode;
  const { label, options } = uischema;
  const { required } = schema;
  const { disableFuture, disablePast } = options || {};
  const isShowValidation = validationMode === ValidationModeJFC.ValidateAndShow;
  const appliedTimeFormat = Constants.HHN_TIME_FORMAT;
  const appliedDateFormat = Constants.HHN_DATE_FORMAT;
  const isVisitDateDisablePast = disablePast;
  const isVisitDateDisableFuture = disableFuture;
  const appointmentPickerLabel = typeof label === "string" ? label : "Visit";
  const appointmentDateFormat = defaultDateFormat;

  const { visitDateError, visitStartTimeError, visitEndTimeError, extraError } =
    useAppointmentErrors(errors || [], path);

  const [localVisitDate, setLocalVisitDate] = useState<Dayjs | null>(
    data?.visitDate ? dayjs(data.visitDate) : null,
  );

  // Drive the date field from local state so each typed digit (esp. the year)
  // reflects immediately. Re-sync only when external data lands on a different
  // day, so a live recompute doesn't clobber an in-progress entry.
  useEffect(() => {
    const dataDate = data?.visitDate ? dayjs(data.visitDate) : null;
    const inSync =
      (!dataDate && !localVisitDate) ||
      (!!dataDate &&
        !!localVisitDate &&
        dataDate.isSame(localVisitDate, "day"));
    if (!inSync) {
      setLocalVisitDate(dataDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.visitDate]);

  const handleDateChange = (newValue: Dayjs | null) => {
    setLocalVisitDate(newValue);
    const changeHandler = (path: string, formatedValue: Dayjs) => {
      const updatedStartTime =
        data?.visitStartTime &&
        dayjs(formatedValue)
          .set("hour", dayjs(data.visitStartTime).hour())
          .set("minute", dayjs(data.visitStartTime).minute());

      const updatedEndTime =
        data?.visitEndTime &&
        dayjs(formatedValue)
          .set("hour", dayjs(data.visitEndTime).hour())
          .set("minute", dayjs(data.visitEndTime).minute());

      handleChange(path, {
        ...data,
        visitDate: dayjs(formatedValue).valueOf(),
        ...(data?.visitStartTime && {
          visitStartTime: updatedStartTime.valueOf(),
        }),
        ...(data?.visitEndTime && {
          visitEndTime: updatedEndTime.valueOf(),
        }),
      });
    };
    if (newValue) {
      createOnChangeHandler(
        path,
        changeHandler,
        appointmentDateFormat,
      )(newValue);
    }
  };

  const handleStartTimeChange = (newValue: Dayjs | null) => {
    const startTime = dayjs(newValue);
    const endTime = startTime.add(2, "hour");
    handleChange(path, {
      ...data,
      visitStartTime: startTime.valueOf(),
      visitEndTime: endTime.valueOf(),
    });
  };

  const handleEndTimeChange = (newValue: Dayjs | null) => {
    const endTime = dayjs(newValue);
    handleChange(path, {
      ...data,
      visitEndTime: endTime.valueOf(),
    });
  };

  if (!visible) {
    return <></>;
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Grid container spacing={2} style={{ marginBottom: 6 }}>
        <Grid
          size={6}
          sx={{
            marginBottom: "1rem",
          }}
        >
          <DatePicker
            label={`${appointmentPickerLabel} Date`}
            value={localVisitDate}
            onChange={handleDateChange}
            format={appliedDateFormat}
            disablePast={isVisitDateDisablePast}
            disableFuture={isVisitDateDisableFuture}
            slotProps={{
              textField: {
                fullWidth: true,
                required: required?.includes("visitDate"),
                error: isShowValidation && (!!visitDateError || !!extraError),
                helperText: isShowValidation && (visitDateError || extraError),
              },
            }}
          />
        </Grid>
        <Grid size={3}>
          <TimePicker
            referenceDate={data?.visitDate ? dayjs(data.visitDate) : undefined}
            viewRenderers={timePickerViewRenderers}
            label={`${appointmentPickerLabel} Start Time`}
            value={data?.visitStartTime ? dayjs(data.visitStartTime) : null}
            disabled={!data?.visitDate}
            onChange={handleStartTimeChange}
            format={appliedTimeFormat}
            slotProps={{
              textField: {
                fullWidth: true,
                required: required?.includes("visitStartTime"),
                error: isShowValidation && !!visitStartTimeError,
                helperText: isShowValidation && visitStartTimeError,
              },
            }}
          />
        </Grid>
        <Grid size={3}>
          <TimePicker
            referenceDate={data?.visitDate ? dayjs(data.visitDate) : undefined}
            viewRenderers={timePickerViewRenderers}
            label={`${appointmentPickerLabel} End Time`}
            value={data?.visitEndTime ? dayjs(data.visitEndTime) : null}
            disabled={!data?.visitStartTime || !data?.visitDate}
            onChange={handleEndTimeChange}
            format={appliedTimeFormat}
            slotProps={{
              textField: {
                fullWidth: true,
                required: required?.includes("visitEndTime"),
                error: isShowValidation && !!visitEndTimeError,
                helperText: isShowValidation && visitEndTimeError,
              },
            }}
          />
        </Grid>
      </Grid>
      {data?.isError && !data.isConflictAllowed && (
        <FormHelperText error={true}>
          {t("templates.appointmentConflict", { ns: "chat" })}
        </FormHelperText>
      )}
    </LocalizationProvider>
  );
};

export default withJsonFormsControlProps(AppointmentPickerRenderer);
